/* Form-family park: the drawer properties convert with the details-tab round (component-map.md). */
import {
  Editable,
  EditablePreview,
  EditablePreviewProps,
  EditableProps,
  EditableTextarea,
  forwardRef,
  useColorModeValue,
  useEditableState,
  useFormControl,
  useMergeRefs,
} from '@chakra-ui/react';
import { type FocusEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import ResizeTextarea from 'react-textarea-autosize';

const toSingleLine = (value: string) => value.replace(/\s*\n\s*/g, ' ').trim();

type NamePreviewProps = EditablePreviewProps & {
  /** Opens the editor, then calls onAutoEdit so the caller can turn autoEdit off. */
  autoEdit?: boolean;
  onAutoEdit?: () => void;
};

/**
 * The read view starts editing when activated (click, Enter, Space), not when it
 * merely receives focus as Chakra's preview does. Focus returns here on its own after
 * Enter saves, and that is not a request to edit.
 */
const NamePreview = forwardRef<NamePreviewProps, 'span'>(
  ({ autoEdit, onAutoEdit, onFocus, ...props }, ref) => {
    const { onEdit, onCancel, isEditing, isDisabled } = useEditableState();

    // Chakra leaves the editor open when the field is disabled mid-edit, e.g. by a permission refresh.
    useEffect(() => {
      if (isDisabled && isEditing) {
        onCancel();
      }
    }, [isDisabled, isEditing, onCancel]);

    useEffect(() => {
      if (autoEdit) {
        onAutoEdit?.();
        onEdit();
      }
    }, [autoEdit, onAutoEdit, onEdit]);

    return (
      <EditablePreview
        ref={ref}
        onClick={onEdit}
        onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onEdit();
          }
        }}
        onFocus={(event: FocusEvent<HTMLDivElement>) => {
          onFocus?.(event);
          // Stops Chakra's own focus handler, which would start editing.
          event.preventDefault();
        }}
        {...props}
      />
    );
  },
);

type ReactHookFormProps = {
  rhfProps?: UseFormRegisterReturn;
};

type EditableAutoResizeTextareaProps = ReactHookFormProps &
  Omit<EditableProps, 'value' | 'submitOnBlur'> & {
    autoFocusIfEmpty?: boolean;
    borderColor?: string;
    /** The field owns its value; pass the saved name as defaultValue. */
    value?: never;
  };

export const EditableAutoResizeTextarea = forwardRef<EditableAutoResizeTextareaProps, 'div'>(
  (
    {
      fontSize = 'xs',
      fontWeight,
      defaultValue,
      rhfProps,
      isDisabled: _isDisabled,
      color,
      borderColor: propBorderColor,
      autoFocusIfEmpty,
      onSubmit,
      // The field owns its value, so a caller's value (a form's spread field) must not override it.
      value: _value,
      onChange,
      ...rest
    },
    ref,
  ) => {
    // Opens by itself only for a name that is empty when the field first renders, so a name the
    // user clears stays closed when they move on.
    const [autoEdit, setAutoEdit] = useState(!defaultValue && !!autoFocusIfEmpty);
    const [value, setValue] = useState(defaultValue ?? '');
    const [hasTyped, setHasTyped] = useState(false);
    const [savedValue, setSavedValue] = useState(defaultValue);
    // The name this field last submitted, until a saved name comes back.
    const [submittedValue, setSubmittedValue] = useState<string>();
    if (defaultValue !== savedValue) {
      setSavedValue(defaultValue);
      setSubmittedValue(undefined);
      // A name saved elsewhere replaces the shown text, but not text the user has changed.
      if (!hasTyped || value === (submittedValue ?? savedValue ?? '')) {
        setValue(defaultValue ?? '');
      }
    }
    // What the name is, or is about to be once a pending save comes back.
    const latestName = submittedValue ?? defaultValue ?? '';
    const isEmpty = !value;
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const textareaPreviewRef = useRef<HTMLElement>(null);
    const hoverBgColor = useColorModeValue('gray.100', 'gray.600');
    const focusBgColor = useColorModeValue('gray.25', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'rgba(255,255,255,0.16)');
    const placeholderColor = useColorModeValue('gray.400', 'gray.500');
    const formControlProps = useFormControl({});
    const isDisabled = formControlProps.readOnly || _isDisabled;

    const { ref: rhfRef, ...rhfPropsRest } = rhfProps || {};
    const refs = useMergeRefs(textareaRef, rhfRef);

    useEffect(() => {
      const openEditor = () => textareaPreviewRef.current?.click();
      const labelElement = document.querySelector(`label[for="${formControlProps.id}"]`);
      if (labelElement) {
        labelElement.addEventListener('click', openEditor);
      }
      return () => {
        if (labelElement) {
          labelElement.removeEventListener('click', openEditor);
        }
      };
    }, [formControlProps.id]);

    const handleSubmit = (nextValue: string) => {
      setHasTyped(false);
      const singleLine = toSingleLine(nextValue);
      // Compared with the name as stored, so removing a stored line break still saves. While a
      // save is pending every submit is sent: the field can't tell a failed save from a slow one,
      // and sending the same name again retries it.
      if (singleLine === latestName && submittedValue === undefined) {
        setValue(latestName);
        return;
      }
      setValue(singleLine);
      setSubmittedValue(singleLine);
      onSubmit?.(singleLine);
    };

    return (
      <Editable
        ref={ref}
        selectAllOnFocus={false}
        value={value}
        onChange={(next) => {
          setValue(next);
          setHasTyped(true);
          onChange?.(next);
        }}
        fontWeight={fontWeight}
        fontSize={fontSize}
        isDisabled={isDisabled}
        {...rest}
        // Enter saves by blurring the textarea.
        submitOnBlur
        onSubmit={handleSubmit}
        onCancel={(previous) => {
          // Chakra restores the value from when editing began, which a save may have replaced since.
          setValue(latestName);
          setHasTyped(false);
          if (latestName !== previous) {
            onChange?.(latestName);
          }
          rest.onCancel?.(latestName);
        }}
        display="flex"
        alignItems="center"
        h="fit-content"
      >
        <NamePreview
          role="button"
          px={2}
          py={2}
          width="full"
          aria-labelledby={`${formControlProps.id}-label`}
          border="1px"
          borderColor="transparent"
          _hover={isDisabled ? {} : { bg: hoverBgColor, borderColor: hoverBgColor }}
          ref={textareaPreviewRef}
          autoEdit={autoEdit}
          onAutoEdit={() => setAutoEdit(false)}
          color={isEmpty ? placeholderColor : color}
          cursor={isDisabled ? 'not-allowed' : 'text'}
        />
        <EditableTextarea
          id={formControlProps.id}
          px={2}
          py={2}
          minRows={1}
          resize="none"
          border="1px"
          borderColor={propBorderColor ?? borderColor}
          overflow="hidden"
          color={color}
          _focus={{
            boxShadow: 'none',
            bgColor: focusBgColor,
          }}
          as={ResizeTextarea}
          ref={refs}
          {...rhfPropsRest}
          onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
            // The textarea only wraps a long name; Enter saves, unless it confirms an IME composition.
            // Safari reports the Enter that confirms a candidate as keyCode 229, after the composition ended.
            const isImeEnter = event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229;
            if (event.key === 'Enter' && !isImeEnter) {
              event.preventDefault();
              textareaRef.current?.blur();
              setTimeout(() => textareaPreviewRef.current?.focus());
            }
          }}
          onFocus={() =>
            textareaRef.current?.setSelectionRange(
              textareaRef.current.value?.length || 0,
              textareaRef.current.value?.length || 0,
            )
          }
        />
      </Editable>
    );
  },
);
